'use client';

import { animated, easings, useReducedMotion, useSpring } from '@react-spring/web';
import { IconChevronDownFilled } from '@zillow/constellation-icons/react/filled/icon-chevron-down-filled';
import { forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { Icon } from '../icon';
import { ComboboxRootContext } from './context/combobox-root.context';
import { comboboxAdornmentRecipe } from './styles/combobox-adornment.styles';
import type { ComboboxAdornmentPropsInterface } from './types/combobox-adornment.types';

/**
 * @since 10.0.0
 */
export const ComboboxAdornment = forwardRef<HTMLSpanElement, ComboboxAdornmentPropsInterface>(
  (props: ComboboxAdornmentPropsInterface, forwardedRef) => {
    const { children, className, css: cssProp, ...rest } = props;
    const { combobox, size } = useContext(ComboboxRootContext);

    const reduceMotion = useReducedMotion();

    const animatedIconStyles = css({
      display: 'inline-flex',
    });

    const animatedIconProps = useSpring({
      rotate: combobox.open ? 180 : 0,
      config: {
        easing: easings.easeInOutQuart,
        duration: reduceMotion ? 0 : 250,
      },
    });

    const styles = css(comboboxAdornmentRecipe.raw({ size }), cssProp);

    const content = children || (
      <animated.span className={cx('c11n', animatedIconStyles)} style={animatedIconProps}>
        <Icon size="sm" render={<IconChevronDownFilled />} />
      </animated.span>
    );

    return (
      <span
        ref={forwardedRef}
        data-c11n-component="Combobox.Adornment"
        data-c11n-version={__C11N_VERSION__}
        className={cx('c11n', styles, className)}
        {...rest}
      >
        {content}
      </span>
    );
  },
);

ComboboxAdornment.displayName = 'Combobox.Adornment';
